/**
 * @author    Nikko Renolds
 * @package   Trap Tycoon
 * @link      https://github.com/Ni1kko/Trap-Tycoon
 * @copyright (c) Fallox Developments2026.
 * @license   MIT
 * @description Compact Iron Vault interface for cash and valuable storage.
 */

import React, { useState } from "react";
import {
  Landmark,
  ArrowUpCircle,
  ArrowDownCircle,
  ShieldCheck,
  Gem,
  Lock,
  Unlock,
} from "lucide-react";
import BankTransfer from "./BankTransfer";
import { callBackend } from "../utils/api";
import { useNotify } from "./Notification";

export default function Bank({ stats, fetchStats, onTransactionSuccess }) {
  const { addNotification } = useNotify();
  const [amount, setAmount] = useState(0);

  const handleTransaction = async (type) => {
    try {
      const endpoint = type === "deposit" ? "/bank/deposit" : "/bank/withdraw";
      const data = await callBackend(endpoint, {
        method: "POST",
        body: { amount: parseInt(amount), clientStats: stats },
      });
      addNotification(data.message, "success");
      onTransactionSuccess();
      setAmount(0);
    } catch (err) {
      addNotification(err.message, "error");
    }
  };

  const handleItemVault = async (inventoryId, action) => {
    try {
      const endpoint =
        action === "deposit" ? "/bank/deposit-item" : "/bank/withdraw-item";
      const data = await callBackend(endpoint, {
        method: "POST",
        body: { inventoryId },
      });
      addNotification(data.message, "success");
      onTransactionSuccess();
    } catch (err) {
      addNotification(err.message, "error");
    }
  };

  const handItems = (stats.inventory || []).filter(
    (i) => !i.is_vaulted && i.type === "valuable",
  );
  const vaultedItems = (stats.inventory || []).filter((i) => i.is_vaulted);

  return (
    <div className="max-w-4xl mx-auto bg-zinc-950 border-2 border-zinc-900 hover:border-green-900/40 p-6 shadow-2xl transition-all duration-300 font-mono">
      {/* HEADER: More compact padding */}
      <div className="flex items-center justify-between mb-6 border-b border-zinc-800 pb-4">
        <div className="flex items-center gap-3">
          <Landmark size={32} className="text-green-500" />
          <div>
            <h2 className="text-2xl font-black italic text-white uppercase leading-none">
              The Iron Vault
            </h2>
            <p className="text-[9px] text-zinc-600 uppercase tracking-widest flex items-center gap-1 mt-1">
              <ShieldCheck size={10} className="text-blue-500" /> Secure
              Protocol Active
            </p>
          </div>
        </div>
      </div>

      {/* BALANCES: Tighter grid */}
      <div className="grid grid-cols-2 gap-4 mb-6">
        <div className="bg-zinc-900/50 p-4 border border-zinc-800">
          <p className="text-[10px] text-zinc-500 uppercase font-black mb-1">
            Liquid Cash
          </p>
          <p className="text-xl font-black text-green-500">
            ${stats.cash.toLocaleString()}
          </p>
        </div>
        <div className="bg-zinc-900/50 p-4 border border-zinc-800">
          <p className="text-[10px] text-zinc-500 uppercase font-black mb-1">
            Vaulted Assets
          </p>
          <p className="text-xl font-black text-white">
            ${stats.bank_cash.toLocaleString()}
          </p>
        </div>
      </div>

      {/* ACTION ZONE: Horizontal layout to reduce vertical height */}
      <div className="flex flex-col sm:flex-row gap-2 mb-8 bg-black/40 p-3 border border-zinc-800">
        <div className="relative flex-1">
          <input
            type="number"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            className="w-full bg-zinc-950 border border-zinc-800 p-3 text-lg font-black text-white focus:border-green-600 outline-none transition-colors"
            placeholder="0"
          />
        </div>
        <div className="grid grid-cols-2 gap-2">
          <button
            onClick={() => handleTransaction("deposit")}
            className="flex items-center justify-center gap-2 bg-green-600 hover:bg-green-500 text-black font-black px-4 py-2 uppercase italic text-[10px] transition-all"
          >
            <ArrowUpCircle size={16} /> Deposit
          </button>
          <button
            onClick={() => handleTransaction("withdraw")}
            className="flex items-center justify-center gap-2 bg-zinc-800 hover:bg-white hover:text-black text-white font-black px-4 py-2 uppercase italic text-[10px] transition-all"
          >
            <ArrowDownCircle size={16} /> Withdraw
          </button>
        </div>
      </div>

      <div className="flex flex-col sm:flex-row gap-2 mb-8 bg-black/40 p-3 border border-zinc-800">
        <div className="relative flex-1">
          <BankTransfer stats={stats} fetchStats={fetchStats} />
        </div>
      </div>

      {/* VALUABLES STORAGE: Integrated list */}
      <div className="border-t border-zinc-800 pt-6">
        <h3 className="text-sm font-black italic text-zinc-400 uppercase mb-4 flex items-center gap-2">
          <Gem size={14} className="text-blue-400" /> Safety Deposit Boxes
        </h3>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Pockets */}
          <div className="bg-zinc-900/30 border border-zinc-800 p-3">
            <h4 className="text-[9px] text-zinc-600 uppercase mb-3 font-black tracking-tighter">
              Pockets
            </h4>
            <div className="space-y-1">
              {handItems.length === 0 && (
                <p className="text-[9px] italic text-zinc-800 uppercase">
                  No valuables on hand
                </p>
              )}
              {handItems.map((item) => (
                <div
                  key={item.id}
                  className="flex justify-between items-center bg-black/60 p-2 border border-zinc-900"
                >
                  <div className="min-w-0">
                    <p className="text-[10px] font-bold text-white truncate">
                      {item.name}
                    </p>
                    <p className="text-[8px] text-zinc-600">
                      {item.durability}% Cond.
                    </p>
                  </div>
                  <button
                    onClick={() => handleItemVault(item.id, "deposit")}
                    className="p-1.5 bg-zinc-800 hover:bg-blue-600 text-zinc-400 hover:text-white transition-colors"
                  >
                    <Lock size={12} />
                  </button>
                </div>
              ))}
            </div>
          </div>

          {/* Stored */}
          <div className="bg-zinc-900/30 border border-zinc-800 p-3">
            <h4 className="text-[9px] text-blue-900 uppercase mb-3 font-black tracking-tighter">
              Vaulted
            </h4>
            <div className="space-y-1">
              {vaultedItems.length === 0 && (
                <p className="text-[9px] italic text-zinc-800 uppercase">
                  Vault empty
                </p>
              )}
              {vaultedItems.map((item) => (
                <div
                  key={item.id}
                  className="flex justify-between items-center bg-black/60 p-2 border border-zinc-900"
                >
                  <div className="min-w-0">
                    <p className="text-[10px] font-bold text-white truncate">
                      {item.name}
                    </p>
                    <p className="text-[8px] text-blue-900">
                      Secured | {item.durability}%
                    </p>
                  </div>
                  <button
                    onClick={() => handleItemVault(item.id, "withdraw")}
                    className="p-1.5 bg-zinc-800 hover:bg-white hover:text-black text-zinc-400 transition-colors"
                  >
                    <Unlock size={12} />
                  </button>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
